/**
 * Permission utility for role-based access control
 */

export const ROLES = {
  ADMIN: 'admin',
  ORGANIZER: 'organizer'
};

export const PERMISSIONS = {
  // Tournament Management
  CREATE_TOURNAMENT: 'create_tournament',
  EDIT_TOURNAMENT: 'edit_tournament',
  DELETE_TOURNAMENT: 'delete_tournament',
  START_TOURNAMENT: 'start_tournament',
  STOP_TOURNAMENT: 'stop_tournament',
  
  // Player Management
  CREATE_PLAYER: 'create_player',
  EDIT_PLAYER: 'edit_player',
  DELETE_PLAYER: 'delete_player',
  DELETE_ALL_PLAYERS: 'delete_all_players',
  
  // Golf Course Management
  CREATE_GOLF_COURSE: 'create_golf_course',
  EDIT_GOLF_COURSE: 'edit_golf_course',
  DELETE_GOLF_COURSE: 'delete_golf_course',
  
  // Scoring
  ENTER_SCORES: 'enter_scores',
  VIEW_SCOREBOARDS: 'view_scoreboards',
  
  // Administrative Functions
  MASTER_RESET: 'master_reset',
  PLAYER_RESET: 'player_reset',
  TOURNAMENT_RESET: 'tournament_reset',
  VIEW_TRANSACTION_LOGS: 'view_transaction_logs',
  MANAGE_BACKUPS: 'manage_backups',
  MANAGE_USERS: 'manage_users',
  SYSTEM_SETTINGS: 'system_settings',
  
  // User Management
  CREATE_USER: 'create_user',
  EDIT_USER: 'edit_user',
  DELETE_USER: 'delete_user',
  CHANGE_USER_PASSWORD: 'change_user_password',
  SEND_PASSWORD_RESET: 'send_password_reset',
  
  // Tournament Features
  MANAGE_TEE_TIMES: 'manage_tee_times',
  MANAGE_PAYOUTS: 'manage_payouts',
  MANAGE_SKINS: 'manage_skins'
};

// Define role permissions
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    // Full access to everything
    PERMISSIONS.CREATE_TOURNAMENT,
    PERMISSIONS.EDIT_TOURNAMENT,
    PERMISSIONS.DELETE_TOURNAMENT,
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.STOP_TOURNAMENT,
    PERMISSIONS.CREATE_PLAYER,
    PERMISSIONS.EDIT_PLAYER,
    PERMISSIONS.DELETE_PLAYER,
    PERMISSIONS.DELETE_ALL_PLAYERS,
    PERMISSIONS.CREATE_GOLF_COURSE,
    PERMISSIONS.EDIT_GOLF_COURSE,
    PERMISSIONS.DELETE_GOLF_COURSE,
    PERMISSIONS.ENTER_SCORES,
    PERMISSIONS.VIEW_SCOREBOARDS,
    PERMISSIONS.MASTER_RESET,
    PERMISSIONS.PLAYER_RESET,
    PERMISSIONS.TOURNAMENT_RESET,
    PERMISSIONS.VIEW_TRANSACTION_LOGS,
    PERMISSIONS.MANAGE_BACKUPS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.SYSTEM_SETTINGS,
    PERMISSIONS.CREATE_USER,
    PERMISSIONS.EDIT_USER,
    PERMISSIONS.DELETE_USER,
    PERMISSIONS.CHANGE_USER_PASSWORD,
    PERMISSIONS.SEND_PASSWORD_RESET,
    PERMISSIONS.MANAGE_TEE_TIMES,
    PERMISSIONS.MANAGE_PAYOUTS,
    PERMISSIONS.MANAGE_SKINS
  ],
  [ROLES.ORGANIZER]: [
    // Limited access - no destructive operations
    PERMISSIONS.CREATE_TOURNAMENT,
    PERMISSIONS.EDIT_TOURNAMENT,
    // NO DELETE_TOURNAMENT
    PERMISSIONS.START_TOURNAMENT,
    PERMISSIONS.STOP_TOURNAMENT,
    PERMISSIONS.CREATE_PLAYER,
    PERMISSIONS.EDIT_PLAYER,
    PERMISSIONS.DELETE_PLAYER,
    // NO DELETE_ALL_PLAYERS
    PERMISSIONS.CREATE_GOLF_COURSE,
    PERMISSIONS.EDIT_GOLF_COURSE,
    PERMISSIONS.DELETE_GOLF_COURSE,
    PERMISSIONS.ENTER_SCORES,
    PERMISSIONS.VIEW_SCOREBOARDS,
    // NO MASTER_RESET
    // NO PLAYER_RESET
    // NO TOURNAMENT_RESET
    // NO VIEW_TRANSACTION_LOGS
    // NO MANAGE_BACKUPS
    // NO MANAGE_USERS
    // NO SYSTEM_SETTINGS
    PERMISSIONS.MANAGE_TEE_TIMES,
    PERMISSIONS.MANAGE_PAYOUTS,
    PERMISSIONS.MANAGE_SKINS
  ]
};

/**
 * Check if a user has a specific permission
 * @param {string} userRole - The user's role
 * @param {string} permission - The permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

/**
 * Check if a user is an administrator
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is an admin
 */
export const isAdmin = (userRole) => {
  return userRole === ROLES.ADMIN;
};

/**
 * Check if a user is an organizer
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user is an organizer
 */
export const isOrganizer = (userRole) => {
  return userRole === ROLES.ORGANIZER;
};

/**
 * Get all permissions for a role
 * @param {string} userRole - The user's role
 * @returns {Array} - Array of permissions for the role
 */
export const getRolePermissions = (userRole) => {
  return ROLE_PERMISSIONS[userRole] || [];
};

/**
 * Check if a user can perform destructive operations
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user can perform destructive operations
 */
export const canPerformDestructiveOperations = (userRole) => {
  return hasPermission(userRole, PERMISSIONS.MASTER_RESET);
};

const Permissions = {
  ROLES,
  PERMISSIONS,
  hasPermission,
  isAdmin,
  isOrganizer,
  getRolePermissions,
  canPerformDestructiveOperations
};

export default Permissions;
